import { Button, Field, Modal } from "components";
import useAnalytics from "hooks/analytics";
import useLanguage from "hooks/language";
import React, { useEffect, useState } from "react";
import { XCircle } from "react-feather";
import { NotificationManager } from "react-notifications";
import {
    trackBookingFlowLocationPopupClosed,
    trackBookingFlowLocationPopupLabChanged,
    trackBookingFlowLocationPopupLocationChanged,
    trackBookingFlowLocationPopupPopoverClicked,
    trackBookingFlowLocationPopupSubmitted,
    trackBookingFlowLocationPopupToggleClicked,
    trackBookingFlowLocationPopupViewed,
} from "utils/analytics/booking-flow-tracking";
import { OpenModalTrigger, UserActionEvent } from "utils/analytics/events";
import { normalizePostalCode } from "utils/format";
import { LabType } from "utils/lab-type-helper";
import { isValidPostalCode } from "utils/validation";
import { LocationType, UserGeolocation } from ".";
import { ReactComponent as BlueEllipse } from "../../assets/images/ellipse-blue.svg";

export type LocationModalResult =
    | { postalCode: string; geolocation: undefined }
    | { postalCode: undefined; geolocation: UserGeolocation };

export default function AvailabilityLocationModal({
    postalCode,
    isModalOpen,
    setIsModalOpen,
    onSubmitLocation,
    openModalTrigger,
    labType,
    setLabType,
    locationType,
    setLocationType,
    useBookingFlowLocationEvents = false,
}: {
    postalCode: string | undefined;
    isModalOpen: boolean;
    setIsModalOpen: (value: boolean) => void;
    onSubmitLocation: (result: LocationModalResult) => void;
    openModalTrigger: OpenModalTrigger;
    labType: LabType | undefined;
    setLabType: (arg: LabType) => void;
    locationType: LocationType;
    setLocationType: (arg: LocationType) => void;
    useBookingFlowLocationEvents?: boolean;
}): React.ReactElement {
    const { translations } = useLanguage();
    const [analytics] = useAnalytics();
    const [geolocation, setGeolocation] = useState<
        UserGeolocation | undefined
    >();
    const normalizedPostalCode = postalCode
        ? normalizePostalCode(postalCode)
        : undefined;
    const [inputValue, setInputValue] = useState<string | undefined>(
        normalizedPostalCode
    );

    const [isRAMQPopoverOpen, setIsRAMQPopoverOpen] = useState<boolean>(false);
    const isValidPostalCodeInput =
        !!inputValue && isValidPostalCode(inputValue);

    useEffect(() => {
        if (!isModalOpen) return;

        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupViewed(analytics, openModalTrigger);
        } else {
            analytics?.cdp()?.trackEvent(UserActionEvent.LocationPopupViewed, {
                trigger: openModalTrigger,
            });
        }
    }, [isModalOpen, useBookingFlowLocationEvents]);

    const onSubmit = () => {
        let method: "postal code" | "geolocation" | undefined;

        if (inputValue && isValidPostalCode(inputValue)) {
            method = "postal code";

            const normalizedPostalCode = normalizePostalCode(inputValue);

            onSubmitLocation({
                postalCode: normalizedPostalCode,
                geolocation: undefined,
            });
            setGeolocation(undefined);
        } else if (geolocation) {
            method = "geolocation";
            onSubmitLocation({
                postalCode: undefined,
                geolocation,
            });
        }

        const eventProperties = {
            method,
            lab: labType,
        };
        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupSubmitted(analytics, eventProperties);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(
                    UserActionEvent.LocationPopupSubmitted,
                    eventProperties
                );
        }

        setIsModalOpen(false);
    };

    const postalCodeError =
        inputValue && !isValidPostalCodeInput
            ? translations.questionnaireComponent.zipCodeInvalidError
            : null;

    function handleGetCurrentPositionSuccess(pos: GeolocationPosition) {
        setGeolocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
        });
        setInputValue(undefined);
    }

    const isGeolocationEnabled = "geolocation" in navigator;
    function handleGetCurrentPositionError() {
        setLocationType("postalCode");
        setGeolocation(undefined);

        NotificationManager.error(
            translations.availabilityPublicPage.locationAccessError
        );
    }
    const handleLocationSelection = () => {
        if (isGeolocationEnabled && locationType === "postalCode") {
            navigator.geolocation.getCurrentPosition(
                handleGetCurrentPositionSuccess,
                handleGetCurrentPositionError,
                {
                    maximumAge: 0,
                }
            );
            setLocationType("location");
        } else {
            setLocationType("postalCode");
            setInputValue(normalizedPostalCode);
        }

        const selection =
            isGeolocationEnabled && locationType === "postalCode"
                ? "geolocation"
                : "postal code";
        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupLocationChanged(analytics, selection);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.LocationPopupLocationChanged, {
                    selection,
                });
        }
    };

    const closeModal = () => {
        if (!isModalOpen) return;

        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupClosed(analytics);
        } else {
            analytics?.cdp()?.trackEvent(UserActionEvent.LocationPopupClosed);
        }
        setIsModalOpen(false);
    };

    const onSelectLabType = (labType: LabType) => {
        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupLabChanged(analytics, labType);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.LocationPopupLabChanged, {
                    selection: labType,
                });
        }
        setLabType(labType);
    };

    const onPopoverToggle = () => {
        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupToggleClicked(analytics);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.LocationPopupToggleClicked);
        }
    };

    const onPopoverCtaClick = () => {
        if (useBookingFlowLocationEvents) {
            trackBookingFlowLocationPopupPopoverClicked(analytics);
        } else {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.LocationPopupPopoverClicked);
        }
    };

    const shouldDisableSearchButton = () => {
        if (locationType === "postalCode" && inputValue) {
            return !isValidPostalCodeInput;
        }

        return false;
    };

    return (
        <Modal
            showHeader={false}
            visible={isModalOpen}
            className="availabilityModal"
            onVisibilityChange={(visible) => {
                if (!visible) closeModal();
            }}
        >
            <div className="availability-geolocation-modal__container__button--close">
                <button
                    aria-label={translations.common.close}
                    className="availability-geolocation-modal__button--close"
                    onClick={closeModal}
                >
                    <XCircle aria-hidden="true" focusable="false" />
                </button>
            </div>
            <div className="availability-geolocation-modal__container">
                <div className="availability-geolocation-modal__title-container">
                    <div className="availability-geolocation-modal__title-pin-text">
                        📍
                    </div>

                    <div className="availability-geolocation-modal__title">
                        {translations.availabilityPublicPage.findClinic1}
                    </div>
                </div>

                <div className="availability-geolocation-modal__content">
                    <div className="availability-geolocation-modal__header">
                        <h3 className="availability-geolocation-modal__location-title">
                            {translations.availabilityPublicPage[locationType]}
                        </h3>
                        {isGeolocationEnabled && (
                            <div className="availability-geolocation-modal__location-container">
                                <span>{translations.common.or}</span>
                                <button
                                    onClick={handleLocationSelection}
                                    className="availability-location-modal__button"
                                >
                                    {
                                        translations.availabilityPublicPage[
                                            locationType === "postalCode"
                                                ? "useMyLocation"
                                                : "useMyPostalCode"
                                        ]
                                    }
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="availability-geolocation-modal__input-holder">
                        {locationType === "postalCode" ? (
                            <Field
                                maxlength={7}
                                value={inputValue}
                                placeholder="H2M 1M2"
                                onPressEnter={() => {
                                    if (postalCodeError) {
                                        return;
                                    }
                                    onSubmit();
                                }}
                                error={postalCodeError}
                                onChange={(e) => {
                                    setInputValue(e.target.value.toUpperCase());
                                }}
                            />
                        ) : (
                            <div className="input-holder__location">
                                <BlueEllipse
                                    style={{
                                        backgroundColor: "#0004FF33",
                                        padding: "3px",
                                        borderRadius: "50%",
                                    }}
                                />
                                <span>
                                    {
                                        translations.availabilityPublicPage
                                            .yourLocation
                                    }
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <Button
                    type="primary"
                    disabled={shouldDisableSearchButton()}
                    onClick={onSubmit}
                    className="availability-geolocation-modal__button"
                >
                    {translations.availabilityPublicPage.search}{" "}
                </Button>
            </div>
        </Modal>
    );
}
